<template>
  <div
    class="user-dropdown-wrapper animate-dropdown relative z-50 flex cursor-pointer items-start gap-[6px] ds:items-center tracking-[0.2px] text-[rgba(255,255,255,0.5)] subpixel-antialiased hover:text-white ds:text-(--ds-text-secondary) ds:hover:text-(--ds-text-primary)"
    :class="[browserWidth < 1024 ? isShownOnMobile && 'show' : '']"
  >
    <button
      type="button"
      aria-label="User dropdown button"
      class="animate-dropdown-btn-focus group relative top-[-2px] flex cursor-pointer items-center ds:top-0"
      @click="isShownOnMobile = !isShownOnMobile"
    >
      <!-- Design-system look: a white line user icon on a brand-violet disc instead of the avatar. -->
      <span
        class="hidden size-[42px] items-center justify-center rounded-full bg-(--ds-action-brand) text-(--ds-color-white) transition-colors duration-150 group-hover:bg-(--ds-color-violet-d300) ds:flex"
        data-test="user-menu-icon"
      >
        <FontAwesomeIcon :icon="['fas', 'user']" class="text-[18px]" />
      </span>
      <div class="mr-3 shrink-0 ds:hidden">
        <Avatar
          class="h-[33px] w-[33px] shrink-0"
          :size="'33px'"
          :allow-update="false"
          :allow-delete="false"
          :initial-value="user.avatar"
        />
      </div>

      <span
        v-if="!pageHaveEvent"
        class="mr-[8px] hidden whitespace-nowrap text-sm font-semibold text-[rgba(255,255,255,0.5)] group-hover:text-white 2xl:block ds:hidden!"
        v-text="`${user.first_name} ${user.last_name}`"
      />

      <FontAwesomeIcon
        :icon="['fas', 'angle-down']"
        class="hidden text-sm text-[rgba(255,255,255,0.5)] group-hover:text-white 2xl:block ds:hidden!"
      />
    </button>
    <!-- Design-system look: opens down from the headline row at the top right of the page. -->
    <div
      class="dropdown-div absolute top-[30px] right-0 w-[200px] overflow-y-auto rounded-[0_0_5px_5px] text-fg-cool subpixel-antialiased"
      :class="
        inHeader
          ? 'ds:top-full ds:overflow-visible'
          : 'ds:top-auto ds:right-auto ds:bottom-0 ds:left-full'
      "
    >
      <div
        class="h-full pt-[26px] pb-1 pr-1"
        :class="inHeader ? 'ds:pt-2 ds:pr-0' : 'ds:pt-0 ds:pl-3'"
      >
        <div
          class="bg-white p-[18px_15px_10px_15px] shadow-[0_5px_6px_rgb(52,58,64,0.10)] overflow-y-auto max-h-[500px]"
          :class="
            inHeader &&
            'ds:rounded-(--ds-radius-md) ds:border ds:border-(--ds-border-app) ds:shadow-(--ds-elevation-raised)'
          "
        >
          <p
            role="heading"
            aria-level="2"
            class="mb-[8px] text-tiny font-bold uppercase tracking-[1px] text-[#3f1e59] opacity-[0.4]"
            v-text="`${$t('common.welcome')} ${user.first_name}`"
          />

          <div class="block lg:hidden">
            <button
              type="button"
              aria-label="Notifications"
              class="py-[10px] px-[6px] mx-[-6px] hover:bg-surface-violet hover:text-primary transition-all ease-in-out duration-200 rounded"
              @click="
                () => {
                  clickNotif()
                  isShownOnMobile = false
                }
              "
            >
              <p class="text-xs font-bold">{{ $t('common.notifications') }}</p>
            </button>
            <div class="border-b border-dashed border-line-gray my-2"></div>
          </div>

          <div class="hidden lg:block">
            <Link
              role="button"
              :aria-label="$t('common.settings')"
              class="block py-[10px] px-[6px] mx-[-6px] hover:bg-surface-violet hover:text-primary transition-all ease-in-out duration-200 rounded"
              :href="useRoute('account.settings.show')"
              @click="isShownOnMobile = false"
            >
              <p class="text-xs font-bold" v-text="$t('common.settings')" />
            </Link>
            <div class="border-b border-dashed border-line-gray my-2"></div>
          </div>

          <div class="block lg:hidden">
            <Link
              role="button"
              :aria-label="$t('common.my_profile')"
              class="block py-[10px] px-[6px] mx-[-6px] hover:bg-surface-violet hover:text-primary transition-all ease-in-out duration-200 rounded"
              :href="useRoute('account.settings.show')"
              @click="isShownOnMobile = false"
            >
              <p class="text-xs font-bold" v-text="$t('common.my_profile')" />
            </Link>
            <div class="border-b border-dashed border-line-gray my-2"></div>
          </div>
          <div v-if="usePermission('company:view')" class="block lg:hidden">
            <Link
              :aria-label="$t('settings.tab.my_company')"
              class="block py-[10px] px-[6px] mx-[-6px] hover:bg-surface-violet hover:text-primary transition-all ease-in-out duration-200 rounded"
              :href="useRoute('companies.show')"
              @click="isShownOnMobile = false"
            >
              <p
                class="text-xs font-bold"
                v-text="$t('settings.tab.my_company')"
              />
            </Link>
            <div class="border-b border-dashed border-line-gray my-2"></div>
          </div>
          <div v-if="usePermission('user:list')" class="block lg:hidden">
            <Link
              :aria-label="$t('settings.my_users')"
              class="block py-[10px] px-[6px] mx-[-6px] hover:bg-surface-violet hover:text-primary transition-all ease-in-out duration-200 rounded"
              :href="useRoute('companies.users.list')"
              @click="isShownOnMobile = false"
            >
              <p class="text-xs font-bold" v-text="$t('settings.my_users')" />
            </Link>
            <div class="border-b border-dashed border-line-gray my-2"></div>
          </div>
          <div v-if="usePermission('team:list')" class="hidden">
            <Link
              :aria-label="$t('team.my_teams')"
              class="block py-[10px] px-[6px] mx-[-6px] hover:bg-surface-violet hover:text-primary transition-all ease-in-out duration-200 rounded"
              :href="useRoute('users.teams.index')"
              @click="isShownOnMobile = false"
            >
              <p class="text-xs font-bold" v-text="$t('team.my_teams')" />
            </Link>
            <div class="border-b border-dashed border-line-gray my-2"></div>
          </div>
          <div v-if="usePermission('company:view')" class="block lg:hidden">
            <!-- With the Marketplace „Mein Abonnement" becomes „Meine Produkte" there (A3). -->
            <Link
              :aria-label="subscriptionEntry.label"
              class="block py-[10px] px-[6px] mx-[-6px] hover:bg-surface-violet hover:text-primary transition-all ease-in-out duration-200 rounded"
              :href="subscriptionEntry.href"
              data-test="user-menu-subscription"
              @click="isShownOnMobile = false"
            >
              <p class="text-xs font-bold" v-text="subscriptionEntry.label" />
            </Link>
            <div class="border-b border-dashed border-line-gray my-2"></div>
          </div>
          <div
            v-if="
              usePermission('company:flowmate:view') &&
              useFeature('flowmate_integration_center')
            "
            class="block lg:hidden"
          >
            <Link
              :aria-label="$t('common.flowmate_integration_center')"
              class="block py-[10px] px-[6px] mx-[-6px] hover:bg-surface-violet hover:text-primary transition-all ease-in-out duration-200 rounded"
              :href="useRoute('companies.flowmate.show')"
              @click="isShownOnMobile = false"
            >
              <p
                class="text-xs font-bold"
                v-text="$t('common.flowmate_integration_center')"
              />
            </Link>
            <div class="border-b border-dashed border-line-gray my-2"></div>
          </div>
          <div
            v-if="usePermission('company:view') && hasAPIAccess()"
            class="block lg:hidden"
          >
            <Link
              :aria-label="$t('apiKey')"
              class="block py-[10px] px-[6px] mx-[-6px] hover:bg-surface-violet hover:text-primary transition-all ease-in-out duration-200 rounded"
              :href="useRoute('apiKeys.list')"
              @click="isShownOnMobile = false"
            >
              <p class="text-xs font-bold" v-text="$t('apiKey')" />
            </Link>
            <div class="border-b border-dashed border-line-gray my-2"></div>
          </div>
          <div v-if="usePage().props._companies.length > 1">
            <div
              class="block py-[10px] px-[6px] mx-[-6px] hover:bg-surface-violet hover:text-primary transition-all ease-in-out duration-200 rounded"
              @click="
                () => {
                  showCompanySwitcher = true
                  isShownOnMobile = false
                }
              "
            >
              <p
                class="text-xs font-bold"
                v-text="$t('common.switch_company')"
              />
            </div>
            <div class="border-b border-dashed border-line-gray my-2"></div>
          </div>
          <div class="block lg:hidden">
            <a
              :aria-label="$t('common.help_center')"
              :href="useHelpCenterUrl()"
              target="_blank"
              rel="noopener"
              class="block py-[10px] px-[6px] mx-[-6px] hover:bg-surface-violet hover:text-primary transition-all ease-in-out duration-200 rounded"
              @click="isShownOnMobile = false"
            >
              <p class="text-xs font-bold" v-text="$t('common.help_center')" />
            </a>
            <div class="border-b border-dashed border-line-gray my-2"></div>
          </div>
          <!-- Appearance, in the shape of the language group below it: both are "how the Suite
               presents itself", and showing the two looks as a pair of options says what you get
               far better than a switch whose off state has no name. Rendered whenever the company
               has the design system, including while the user is on the classic look - that is
               exactly when they need the way back. -->
          <div v-if="designSystemAvailable" data-test="appearance-switch">
            <p
              role="heading"
              aria-level="3"
              class="mb-1 text-micro font-bold uppercase tracking-[1px] text-fg-mauve ds:text-(--ds-text-secondary)"
              v-text="$t('appearance.title')"
            />
            <button
              v-for="option in appearanceOptions"
              :key="option.key"
              type="button"
              class="mx-[-6px] block w-full rounded px-[6px] py-[6px] text-left transition-all duration-200 ease-in-out hover:bg-surface-violet hover:text-primary"
              :class="option.active && 'text-primary'"
              :aria-current="option.active ? 'true' : undefined"
              :data-test="`appearance-${option.key}`"
              @click="setAppearance(option.designSystem)"
            >
              <span class="text-xs font-bold" v-text="option.label" />
            </button>
            <div class="my-2 border-b border-dashed border-line-gray"></div>
          </div>
          <!-- Design-system look: the sidebar has no language switcher, so it lives here. -->
          <Locales v-if="designSystem" v-slot="{ list, current, change }">
            <p
              role="heading"
              aria-level="3"
              class="mb-1 text-micro font-bold uppercase tracking-[1px] text-(--ds-text-secondary)"
              v-text="$t('common.language')"
            />
            <button
              v-for="{ code, label } in list"
              :key="code"
              type="button"
              class="block w-full py-[6px] px-[6px] mx-[-6px] text-left rounded hover:bg-surface-violet hover:text-primary transition-all ease-in-out duration-200"
              :class="code === current.code && 'text-primary'"
              :aria-current="code === current.code ? 'true' : undefined"
              @click="change(code)"
            >
              <span class="text-xs font-bold" v-text="label" />
            </button>
            <div class="border-b border-dashed border-line-gray my-2"></div>
          </Locales>
          <div
            class="block cursor-pointer py-[10px] px-[6px] mx-[-6px] hover:bg-surface-violet hover:text-primary transition-all ease-in-out duration-200 rounded"
            @click="
              () => {
                logout()
                isShownOnMobile = false
              }
            "
          >
            <p class="text-xs font-bold" v-text="$t('common.logout')" />
          </div>
          <div class="border-b border-dashed border-line-gray my-2"></div>
        </div>
      </div>
    </div>
  </div>

  <SuspenseWrapper v-if="showCompanySwitcher">
    <SwitchCompanyModal @close="showCompanySwitcher = false" />
  </SuspenseWrapper>
</template>

<script setup>
import { useAppearance } from '@/composables/appearance'
import { useDesignSystem } from '@/composables/designSystem'
import Avatar from '@/Components/Avatar.vue'
import SwitchCompanyModal from '@/Components/Users/SwitchCompanyModal.vue'
import SuspenseWrapper from '@/Components/common/SuspenseWrapper.vue'
import { usePermission } from '@/composables/permission'
import { useRoute } from '@/composables/route'
import { useHelpCenterUrl } from '@/composables/menu'
import { router, Link, usePage } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import { hasAPIAccess } from '@/composables/subscription'
import { useFeature } from '@/composables/feature'
import Locales from '@/Components/Headless/Locales'
import { useI18n } from 'vue-i18n'

defineProps({
  /** Sits in the design-system headline row (HeaderActions) rather than the classic header. */
  inHeader: {
    type: Boolean,
    default: false,
  },
})

const { t } = useI18n()
const designSystem = useDesignSystem()

/** „Mein Abonnement" in the settings, or „Meine Produkte" in the Marketplace when it is on (A3). */
const subscriptionEntry = computed(() =>
  useFeature('marketplace')
    ? {
        label: t('marketplace.nav.products'),
        href: useRoute('marketplace.index', { view: 'products' }),
      }
    : {
        label: t('common.my_subscription'),
        href: useRoute('companies.subscriptions.show'),
      },
)
const pageHaveEvent = computed(() => usePage().props.event)
const user = computed(() => usePage().props._user)

const {
  available: designSystemAvailable,
  options: appearanceOptions,
  setAppearance: switchAppearance,
} = useAppearance()

const setAppearance = (useDesignSystem) => {
  isShownOnMobile.value = false
  switchAppearance(useDesignSystem)
}
const showCompanySwitcher = ref(false)
const isShownOnMobile = ref(false)
const browserWidth = window.innerWidth

const logout = () =>
  router.post(useRoute('logout'), null, {
    onSuccess: () => {
      window.zE('messenger', 'logoutUser')
      window.zE('messenger', 'hide')
    },
  })

const clickNotif = () => {
  document.querySelector('body').click()
  document.getElementById('notif-menu-btn').click()
}

onclick = (e) => {
  let el = document.querySelector('.user-dropdown-wrapper')
  if (el) {
    const isClickInside = el.contains(e.target)
    if (!isClickInside) {
      isShownOnMobile.value = false
    }
  }
}
</script>
